import {
    ChatBubble,
    ChatItem,
    ChatChip,
} from '../chat-window/chat-window.component';
import { ChatOption } from '../../models/chat.models';

export interface CategorySnapshot {
    items: ChatItem[];
    chips: ChatChip[];
    visitedNodeIds: string[];
    childNodeCount: number;
    excludedNodeCount: number;
    currentNodeId: string;
}

/**
 * Pure, Angular-free conversation logic for the chip-driven chat widget.
 *
 * Everything in this module is side-effect free: given inputs it returns
 * outputs and never touches component state, signals, or the DOM. This keeps
 * the tree-walk decisions explicit and independently reasonable/testable,
 * leaving the component to own only reactivity and request orchestration.
 */

export const RETRY_CHIP_ID = '__retry__';
export const CLOSE_CHIP_ID = '__close__';

export const TYPING_ITEM: ChatItem = { kind: 'typing' };

export const CATEGORY_REENTRY_MESSAGE_KEY = 'help-faq.prompts.REENTRY';

export type WidgetState = 'closed' | 'opened' | 'minimized';

export type FailedRequest =
    | { kind: 'start' }
    | { kind: 'select'; chip: ChatChip };

export type ChipIntent =
    | 'root-reset'
    | 'category-change'
    | 'static-yes'
    | 'static-no'
    | 'static-close'
    | 'leaf-selection';

const STATICYES = 'static-yes';
const STATICNO = 'static-no';
const ROOT = 'root';

export function classifyChip(chip: ChatChip, rootNodeTargetId: string): ChipIntent {
    if (chip.parentNodeId === ROOT && chip.targetNodeId === ROOT) {
        return 'root-reset';
    }
    if (chip.id === STATICYES) {
      return STATICYES;
    }
    if (chip.id === STATICNO) {
      return STATICNO;
    }
    if (chip.id === CLOSE_CHIP_ID) {
      return 'static-close';
    }
    if (chip.parentNodeId === ROOT) {
        return 'category-change';
    }

    void rootNodeTargetId;
    return 'leaf-selection';
}
export function buildConversationContext(items: ChatItem[]): string | null {
    const bubbles = items.filter(
        (item): item is { kind: 'bot' | 'user'; text: string } =>
            item.kind === 'bot' || item.kind === 'user'
    );
    if (bubbles.length === 0) return null;
    return bubbles
        .slice(-10)
        .map((b) => `${b.kind === 'bot' ? 'Assistant' : 'User'}: ${b.text}`)
        .join('\n');
}
export function mapOptionsToChips(
    options: ChatOption[],
    currentNodeId: string,
    visitedNodeIds: readonly string[],
    excludeNodeIds: readonly string[] = []
): ChatChip[] {
    return options
        .filter((option) => !visitedNodeIds.includes(option.targetNodeId))
        .filter((option) => !excludeNodeIds.includes(option.targetNodeId))
        .map((option, index) => ({
            id: `opt-${index}-${option.targetNodeId}`,
            label: `help-faq.questions.${option.targetNodeId}`,
            targetNodeId: option.targetNodeId,
            parentNodeId: currentNodeId,
        }));
}

export function createDisplayBubble(
    kind: 'bot' | 'user',
    textKey: string,
    params?: Record<string, unknown>,
): ChatBubble {
    return { kind, text: textKey, textKey, params };
}

export function buildFollowUpChips(categoryNodeId: string): ChatChip[] {
    return [
      { id: STATICYES, label: 'help-faq.chat.YES', parentNodeId: ROOT, targetNodeId: categoryNodeId },
      { id: STATICNO, label: 'help-faq.chat.NO', parentNodeId: ROOT, targetNodeId: categoryNodeId },
    ];
}

export function hasRemainingChildren(childNodeCount: number, visitedCount: number, excludedCount = 0): boolean {
    return (childNodeCount - excludedCount) > visitedCount;
}
